import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Image,
  Input,
  Row,
  Select,
  Spin,
  message,
} from 'antd';
import locale from 'antd/es/date-picker/locale/es_ES';
import { DocumentCard, PhoneInput } from 'cmt-ui-kit';
import moment from 'moment';
import 'moment/locale/es';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { getGenders, removeAsset } from '../../services/general';
import { patientsService } from "../../services/patient";
import { base64ToStream, uploadAssetController } from '../../utils/fileUtils';
import PatientPhotoButtons from '../components/PatientPhotoButtons';
import WebcamModal from '../components/WebcamModal';
import usePatientPhoto from '../components/hooks/usePatientPhoto';
import { getCookie } from './../../services/cookies';
import HeaderDetail from './components/HeaderDetail';
import countrySettings from "./countrySettings.json";

const { Option } = Select;
const { format } = require('rut.js');


const PatientSettingsPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState({
    fetch: false,
    genders: false,
    idCard: false,
    form: false
  });
  const countryCode = getCookie('country_code')
  const [documentType, setDocumentType] = useState(countryCode === 'CL' ? 'rut' : 'dni');
  const [patientDetails, setPatientDetails] = useState(false);
  const [genders, setGenders] = useState();
  const [form] = Form.useForm();
  const patientPhoto = usePatientPhoto({ setLoading, countryCode, form });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchPatientDetails = useCallback(async () => {
    try {
      setLoading(loading => { return { ...loading, fetch: true } });
      const [response] = await patientsService.getPatientData({ patientId: id });

      if (response) {
        form.setFieldsValue({
          documentType: response.document_type?.toLowerCase(),
          documentNumber: response.document_number,
          names: response.names,
          lastName: response.last_name,
          gender: response.gender && parseInt(response.gender),
          birthdate: response.birthdate && moment(response.birthdate, 'YYYY-MM-DD'),
          phone: PhoneInput.formatPhone(response.phone, countryCode),
          position: response.position,
          email: response.email,
        });
        setPatientDetails(true);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: `Ocurrió un error al obtener datos del paciente`,
        style: {
          marginTop: '35vh'
        }
      });
    } finally {
      setLoading(loading => { return { ...loading, fetch: false } });
    }
  }, [id]);

  const fetchGenders = async () => {
    setLoading(loading => { return { ...loading, genders: true } });
    try {
      const response = await getGenders();
      setGenders(response);
    } catch (error) {
      message.error({
        content: 'Ocurrió un error al obtener la lista de generos',
        style: {
          marginTop: '30vh',
        },
      });
      console.error(error);
    } finally {
      setLoading(loading => { return { ...loading, genders: false } });
    }
  };

  const confirmRefreshPage = (event) => {
    event.preventDefault();

    const message = '¿Estás seguro que deseas abandonar?';

    return event.returnValue = message;
  };

  useEffect(() => {
    !patientDetails && fetchPatientDetails();
    !genders && fetchGenders();
    !patientPhoto.imageSrc && patientPhoto.fetchPatientIdCard({ id });
    window.addEventListener('beforeunload', confirmRefreshPage);
    return () => {
      window.removeEventListener('beforeunload', confirmRefreshPage);
    }
  }, [fetchPatientDetails]);


  const onFinish = async (values) => {
    try {
      setLoading(loading => { return { ...loading, form: true } });
      let params = {
        patientId: id,
        document_type: documentType,
        document_number: documentType === 'rut' ? format(values.documentNumber, { dots: false }) : values.documentNumber,
        names: values.names,
        last_name: values.lastName,
        gender: parseInt(values.gender),
        birthdate: moment(values['birthdate']).format('YYYY-MM-DD'),
        phone: values.phone.includes('+') ? values.phone : `+${values.phone}`,
        position: values.position,
        idCard: !patientPhoto.imageSrc?.includes('missing'),
        email: values.email,
      };
      if (patientPhoto?.imageSrc?.includes('data:image') && !patientPhoto.imageSrc?.includes('missing')) {
        const file = base64ToStream(patientPhoto.imageSrc, 'image/png', 'pictureIdCard.png');
        await uploadAssetController({
          file: file,
          object_field: 'document',
          object_type: 'Patient',
          object_id: params.patientId,
        });
      } else if (!patientPhoto?.imageSrc) {
        await removeAsset({
          field: 'document',
          id: params.patientId,
          type: 'Patient',
        })
      }

      const response = await patientsService.updatePatientData(params);

      if (response === 1) {
        message.success({
          content: `${t('patientPage.settingsPage.patient')} actualizado exitosamente.`,
          style: {
            marginTop: '30vh',
          },
        });
        navigate(`/pacientes/${id}/ver`);
      } else {
        console.error(response);
        message.error({
          content: 'Ocurrió un error al guardar los datos',
          style: {
            marginTop: '30vh',
          },
        });
      }

    } catch (error) {
      console.error(error);
      message.error({
        content: 'Ocurrió un error al guardar los datos',
        style: {
          marginTop: '30vh',
        },
      });
    } finally {
      setLoading(loading => { return { ...loading, form: false } });
    }
  };

  const disabledDate = (current) => {
    const overAge = moment().subtract(8, 'years');
    return current && overAge < current;
  };


  return (
    <Row justify='center' className="patient-details-wrapper mb-50">
      <Col lg={24} xs={24}>
        <HeaderDetail />
        <Row justify='center' className='mt-40'>
          <Spin
            spinning={loading.fetch}
            className="cmt-spin"
          >
            <Row justify="center">
              <Col lg={22} xs={22}>
                <Form
                  form={form}
                  onFinish={onFinish}
                  layout="vertical"
                  size="large"
                  className='cmt-form'
                >
                  <Row justify='center'>
                    <Col lg={17} xs={24}>
                      <Row>
                        <Col lg={14} xs={24}>
                          <Row>
                            <Col lg={24} xs={24}>
                              <h3 className='mb-lg-50 mb-md-30 mb-sm-30 mb-xs-30'>Carnet del {t('patientPage.settingsPage.patient').toLowerCase()}</h3>
                            </Col>
                            <Col lg={24} xs={24}>
                              <PatientPhotoButtons patientPhoto={patientPhoto}>
                                <Spin className='cmt-spin' spinning={loading.idCard}>
                                  <Row>
                                    <Col lg={12} xs={24}>
                                      {
                                        patientPhoto.imageSrc && !patientPhoto.imageSrc?.includes('missing') ?
                                          <Image
                                            src={patientPhoto.imageSrc}
                                            alt='patient'
                                            style={{ width: 276, height: 284 }}
                                            className='mb-20'
                                          />
                                          :
                                          <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description='Sin foto'
                                            className='mb-20'
                                          />
                                      }
                                    </Col>
                                  </Row>
                                </Spin>
                              </PatientPhotoButtons>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row justify='center' className='mt-80'>
                    <Col lg={17} xs={24}>
                      <h3 className='mb-20'>Datos del {t('patientPage.settingsPage.patient').toLowerCase()}</h3>
                    </Col>
                    <Col lg={17} xs={24}>
                      <Row gutter={15} style={{ marginBottom: 24 }}>
                        <Col className='ant-form-item-label' lg={24} xs={24}>
                          <label className='label-text mb-5'>Tipo y número de documento</label>
                        </Col>
                        <Col lg={12} xs={24}>
                          <DocumentCard
                            countryCode={countryCode}
                            setDocumentType={setDocumentType}
                            form={form}
                            type='person'
                          />
                        </Col>
                      </Row>
                      <Row gutter={15}>
                        <Col lg={12} xs={24}>
                          <Form.Item
                            name="names"
                            label="Nombres"
                            rules={[{
                              required: true,
                              message: 'Nombres son requeridos'
                            }]}
                          >
                            <Input className='cmt-input' placeholder='Ingrese nombre/s' />
                          </Form.Item>
                        </Col>
                        <Col lg={12} xs={24}>
                          <Form.Item
                            name="lastName"
                            label="Apellidos"
                            rules={[{
                              required: true,
                              message: 'Apellidos son requeridos'
                            }]}
                          >
                            <Input className='cmt-input' placeholder='Ingrese apellido/s' />
                          </Form.Item>
                        </Col>
                        <Col lg={12} xs={24}>
                          <Form.Item
                            name="gender"
                            label="Sexo"
                            rules={[{
                              required: true,
                              message: 'Género es requerido'
                            }]}
                          >
                            {
                              loading.genders ?
                                <Row justify='center'>
                                  <Spin className='cmt-spin' />
                                </Row>
                                :
                                genders &&
                                <Select className='cmt-select' placeholder="Seleccione sexo">
                                  {
                                    genders.map((item, index) => {
                                      return (
                                        <Option key={index} value={item.id}>
                                          {item.name}
                                        </Option>
                                      )
                                    })
                                  }
                                </Select>
                            }
                          </Form.Item>
                        </Col>
                        <Col lg={12} xs={24}>
                          <Form.Item
                            name="birthdate"
                            label="Fecha de nacimiento"
                            rules={[{
                              required: true,
                              message: 'Fecha de nacimiento es requerido'
                            }]}
                          >
                            <DatePicker
                              className="cmt-date-picker"
                              locale={locale}
                              placeholder="Seleccione fecha de nacimiento"
                              dropdownClassName="custom-dropdown"
                              style={{ width: '100%' }}
                              format="DD-MM-YYYY"
                              disabledDate={disabledDate}
                              inputReadOnly
                            />
                          </Form.Item>
                        </Col>
                        <Col lg={12} xs={24}>
                          <PhoneInput.ReactPhone
                            countryCode={countryCode}
                            form={form}
                            labelText='Teléfono'
                          />
                        </Col>
                        <Col lg={12} xs={24}>
                          <Form.Item
                            name="email"
                            label="Correo"
                            rules={[{
                              required: true,
                              message: 'El correo es requerido'
                            }]}
                          >
                            <Input className='cmt-input' placeholder='Ingrese su correo' />
                          </Form.Item>
                        </Col>
                        <Col lg={12} xs={24}>
                          <Form.Item
                            name="position"
                            label="Cargo"
                            rules={[{
                              required: true,
                              message: 'Cargo es requerido'
                            }]}
                          >
                            <Input className='cmt-input' placeholder='Ingrese cargo' />
                          </Form.Item>
                        </Col>
                        <Col lg={24} xs={24} className='mt-50'>
                          <Row justify='end'>
                            <Col xl={5} lg={6} xs={24}>
                              <Button
                                loading={loading.form}
                                htmlType="submit"
                                className="cmt-button primary"
                                type="primary"
                              >
                                Guardar cambios
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Spin>
        </Row>
      </Col>

      <WebcamModal
        {...patientPhoto}
        countrySettings={countrySettings}
        countryCode={countryCode}
      />
    </Row>
  );
};

export default PatientSettingsPage;
